import React, { useMemo, useState } from "react";
import {
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useLocation, useNavigate } from "react-router-dom";

import { auth, googleProvider, db } from "../firebase";
import "./LoginPage.css";

export default function LoginPage() {
  const [mode, setMode] = useState("login"); // "login" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const location = useLocation();

  const redirectTo = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("from") || "/modules";
  }, [location.search]);


  const handleBack = () => {
  if (window.history.length > 1) {
    navigate(-1);
  } else {
    navigate("/modules");
  }
};



  const upsertUser = async (user, providerLabel) => {
    // IMPORTANT: DO NOT store passwords in Firestore.
    // Firebase Auth stores them securely (hashed) on the auth system.
    await setDoc(
      doc(db, "users", user.uid),
      {
        createdAt: serverTimestamp(),
        lastActive: serverTimestamp(),
        provider: providerLabel,
        email: user.email || null, // optional
      },
      { merge: true }
    );
  };

  const goNext = () => navigate(redirectTo, { replace: true });

  const clearNotices = () => {
    setError("");
    setMsg("");
  };

  const handleGoogle = async () => {
    clearNotices();
    setBusy(true);
    try {
      const res = await signInWithPopup(auth, googleProvider);
      await upsertUser(res.user, "google");
      goNext();
    } catch (err) {
      console.error(err);
      setError(err?.message || "Google sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleEmailPassword = async () => {
    clearNotices();
    if (!email || !password) {
      setError("Enter email and password.");
      return;
    }

    setBusy(true);
    try {
      if (mode === "signup") {
        const res = await createUserWithEmailAndPassword(auth, email, password);
        await upsertUser(res.user, "password");
      } else {
        const res = await signInWithEmailAndPassword(auth, email, password);
        await upsertUser(res.user, "password");
      }
      goNext();
    } catch (err) {
      console.error(err);
      setError(err?.message || "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleForgotPassword = async () => {
    clearNotices();
    if (!email) {
      setError("Enter your email first, then click “Forgot password”.");
      return;
    }

    setBusy(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setMsg("Password reset email sent. Check your inbox.");
    } catch (err) {
      console.error(err);
      setError(err?.message || "Could not send password reset email.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-wrapper">
      <div className="login-card">
        <button
  type="button"
  className="back-btn"
  onClick={handleBack}
  aria-label="Go back"
>
  ← Back
</button>



        <h1>{mode === "signup" ? "Create your account" : "Log in"}</h1>

        <p className="subtitle">
          Save progress and unlock advanced modules.
          <br />
          No SSN. No tax filing. Education only.
        </p>

        <button className="google-btn" onClick={handleGoogle} disabled={busy}>
          Continue with Google
        </button>

        <div className="divider">or</div>

        <div className="mode-tabs">
          <button
            className={`tab ${mode === "login" ? "active" : ""}`}
            onClick={() => {
              clearNotices();
              setMode("login");
            }}
            type="button"
            disabled={busy}
          >
            Log in
          </button>
          <button
            className={`tab ${mode === "signup" ? "active" : ""}`}
            onClick={() => {
              clearNotices();
              setMode("signup");
            }}
            type="button"
            disabled={busy}
          >
            Sign up
          </button>
        </div>

        <input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
        />

        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={busy}
        />

        <button className="primary-btn" onClick={handleEmailPassword} disabled={busy}>
          {mode === "signup" ? "Create account" : "Log in"}
        </button>

        <button
          className="link-btn"
          onClick={handleForgotPassword}
          type="button"
          disabled={busy}
        >
          Forgot password?
        </button>

        {msg && <p className="sent">{msg}</p>}
        {error && <p className="error">{error}</p>}

        <p className="disclaimer">
          Gildarc is educational only. We do not provide tax advice, prepare returns,
          or store sensitive personal financial data.
        </p>
      </div>
    </div>
  );
}
